import { HStack } from "@chakra-ui/react"
import Alert from "@pathwright/ui/src/components/alert/Alert"
import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import Button from "@pathwright/ui/src/components/button/Button"
import Card from "@pathwright/ui/src/components/card/Card"
import CardBlock from "@pathwright/ui/src/components/card/CardBlock"
import FullscreenConfirm from "@pathwright/ui/src/components/confirm/FullscreenConfirm"
import InlineConfirm from "@pathwright/ui/src/components/confirm/InlineConfirm"
import DayTimePicker from "@pathwright/ui/src/components/form/form-day-picker/DayTimePicker"
import ToggleInput from "@pathwright/ui/src/components/form/form-toggle/ToggleInput"
import FormSubmitStatus from "@pathwright/ui/src/components/form/form-utils/FormSubmitStatus"
import useLocalStorage from "@pathwright/ui/src/components/hooks/useLocalStorage"
import Link from "@pathwright/ui/src/components/link/Link"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import { stripHTML } from "@pathwright/ui/src/components/text/utils"
import PathwrightUI from "@pathwright/ui/src/components/ui/PathwrightUI"
import { SECONDARY_GRAY } from "@pathwright/ui/src/components/utils/colors"
import { useQuery } from "@pathwright/web/src/modules/utils/apollo"
import capitalize from "lodash/capitalize"
import first from "lodash/first"
import get from "lodash/get"
import last from "lodash/last"
import pick from "lodash/pick"
import moment from "moment"
import PropTypes from "prop-types"
import React, { useMemo, useRef, useState } from "react"
import { Field, Form, withForm } from "react-connect-form-forked"
import isEqual from "react-fast-compare"
import styled from "styled-components"
import {
  useGroupQuery,
  usePathItemQuery,
  usePathItemsQuery
} from "../../api/generated"
import PathScheduleLabel from "../../path/ui/PathScheduleLabel"
import useRestAPIGetQuery from "../../rest-api/graphql/useRestAPIGetQuery"
import { flattenEdges } from "../../utils/apollo"
import MESSAGES_QUERY from "../graphql/messages-query"
import {
  displayMessageStr,
  getDefaultReminderSubjectBody,
  getInitialMessageValues,
  getMessageOutput,
  useScheduleItem,
  useSubmitMessage
} from "../utils"

const StyledCard = styled(Card)`
  .TimePicker label {
    font-weight: 700;

    @media (max-width: 480px) {
      margin-bottom: 1px;
    }
  }

  .ToggleInput {
    margin-bottom: 1rem;

    @media (max-width: 480px) {
      label {
        margin-bottom: 0;
      }
    }
  }

  .Alert {
    margin-top: 0;
    margin-bottom: 0;
  }
`

const StyledReminderList = styled.ul`
  padding: 0;
  list-style: none;
  margin-bottom: 20px;
  li {
    margin-bottom: 1rem;
    padding-left: 22px;
    a {
      cursor: pointer;
      text-decoration: underline;

      &[disabled] {
        opacity: 0.5;
      }
      .Pathicon:first-of-type {
        position: absolute;
        left: -22px;
      }
    }
    &:not(:last-child) {
      a {
        color: ${SECONDARY_GRAY};
      }
    }
  }
`

const StyledDateTab = styled.a`
  width: 50%;
  display: inline-block;
  text-align: center;
  cursor: pointer;
  text-decoration: none;
  padding: 15px 10px 20px;
  background-color: whitesmoke;
  box-shadow: ${(p) => (p.active ? "inset 0px -5px 0 0 grey" : "")};
  > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: ${(p) => (p.active ? 1 : 0.75)};
    h4 {
      margin: 0;
      margin-bottom: 3px;
      font-weight: ${(p) => (p.active ? "bold" : "normal")};
      /* text-decoration: ${(p) => (p.active ? "underline" : "none")}; */
    }
    small {
      font-weight: ${(p) => (p.active ? "bold" : "normal")};
      color: ${SECONDARY_GRAY};
    }
  }
`

// responsible for updating default subject / body values when pathItem passive date setting changes
const HandleDefaultSubjectBody = ({
  cohortId,
  sourceId,
  pathItem,
  stepDateField,
  reminder,
  reminderDefaults
}) => {
  const handleSubmitReminder = useSubmitMessage({ cohortId, sourceId })

  const passiveKey = `passive_${stepDateField}`
  const passiveValue = pathItem[passiveKey]
  const currentPassiveValueRef = React.useRef(passiveValue)

  const getSubjectBody = (passiveValue) => {
    const nextPathItem = { ...pathItem, [passiveKey]: passiveValue }

    // get default subject and body for the pathItem with the pathItem's next passiveValue
    const [subject, body] = getDefaultReminderSubjectBody(
      nextPathItem,
      stepDateField,
      reminder,
      reminderDefaults
    )
    return [subject, body]
  }

  React.useEffect(() => {
    // not handling initial default, just the default when passiveValue changes from the default value
    if (currentPassiveValueRef.current !== passiveValue) {
      const [currentSubject, currentBody] = getSubjectBody(
        currentPassiveValueRef.current
      )

      if (
        reminder.subject === currentSubject &&
        stripHTML(reminder.body) === currentBody
      ) {
        const [nextSubject, nextBody] = getSubjectBody(passiveValue)

        // reset subject and body values to defaults
        handleSubmitReminder(reminder.id, {
          ...reminder,
          subject: nextSubject,
          body: nextBody
        })
      }
    }

    currentPassiveValueRef.current = passiveValue
  }, [passiveValue])

  return null
}

const ScheduleFormCard = ({ card, sourceId, cohortId }) => {
  const { t, tc, tca } = useTranslate()
  const handleSubmitReminder = useSubmitMessage({ cohortId, sourceId })
  const groupQuery = useGroupQuery({
    variables: { id: cohortId }
  })
  const pathItemQuery = usePathItemQuery({
    variables: {
      source_id: sourceId,
      cohort_id: cohortId
    }
  })
  const parentPathItemQuery = usePathItemQuery({
    variables: {
      source_id: get(pathItemQuery, "data.pathItem.parent_source_id"),
      cohort_id: cohortId
    },
    skip: !get(pathItemQuery, "data.pathItem.parent_source_id")
  })
  const childPathItemsQuery = usePathItemsQuery({
    variables: {
      lessonSourceId: get(pathItemQuery, "data.pathItem.source_id"),
      cohortId
    },
    skip:
      !get(pathItemQuery, "data.pathItem") ||
      !!get(pathItemQuery, "data.pathItem.parent_source_id")
  })
  const messagesQuery = useQuery(MESSAGES_QUERY, {
    variables: {
      message_context: {
        source_id: sourceId,
        cohort_id: cohortId
      },
      type: { eq: "reminder" }
    }
  })
  const handleScheduleItem = useScheduleItem({ cohortId, sourceId })

  const reminderDefaultsQuery = useRestAPIGetQuery("/reminder/defaults/")
  const reminderDefaults = get(reminderDefaultsQuery, "data.restAPIGet")

  const formRef = useRef()

  const [activeDateTab, setActiveDateTab] = useLocalStorage(
    "schedule-active-tab",
    "Start"
  )
  const [pendingConfirmation, setPendingConfirmation] = useState(false)
  const [pendingDeletion, setPendingDeletion] = useState(false)

  const pathItem = get(pathItemQuery, "data.pathItem")
  const parentPathItem = get(parentPathItemQuery, "data.pathItem")
  const childPathItems = flattenEdges(
    get(childPathItemsQuery, "data.pathItems", [])
  ).filter(
    (childPathItem) =>
      // have to filter out the parent in the results
      pathItem && childPathItem.parent_source_id === pathItem.source_id
  )
  const messages = flattenEdges(get(messagesQuery, "data.messages", []))
  const startingMessages = messages.filter(
    (x) => x.based_on_step_date === "starts"
  )
  const endingMessages = messages.filter((x) => x.based_on_step_date === "due")
  const activeMessages =
    activeDateTab === "Start" ? startingMessages : endingMessages

  // get the disabled days for DayTimePicker
  function getDisabledDays() {
    // find the first date that is after now, return Date
    const getDate = (...dates /* [Date string] */) => {
      dates = dates.filter((d) => d).map((d) => moment(d))
      let date = dates.find((d) => d.isAfter())
      if (!date) return dates.length ? new Date() : null
      if (date) return date.toDate() // change moment date to Date date
      return null
    }

    const validateDisabledDays = (disabledDays) => {
      if (disabledDays.before && disabledDays.after) {
        // if after date comes before before date, all days must be disabled
        if (moment(disabledDays.after).isSameOrBefore(disabledDays.before)) {
          return () => true // disable all days
        }
      }

      return disabledDays
    }

    // validate dates based on item
    const forItem = () => {
      switch (activeDateTab) {
        case "Start":
          return {
            // start date cannot come before now
            before: new Date(),
            // start date cannot come after pathItem due date
            after: getDate(pathItem.due) || null
          }
        case "End":
          return {
            // end date cannot come before the pathItem start date OR now
            before: getDate(pathItem.starts) || new Date(),
            // end date can end anytime in the future
            after: null
          }
      }
    }

    // validate based on parent dates
    const forParentedItem = () => {
      switch (activeDateTab) {
        case "Start":
          return {
            // start date cannot come before the parentPathItem start date OR now
            before: getDate(parentPathItem.starts) || new Date(),
            // start date cannot come after pathItem due date OR parentPathItem due date
            after: getDate(pathItem.due, parentPathItem.due) || null
          }
        case "End":
          return {
            // end date cannot come before the pathItem start date OR now
            before: getDate(pathItem.starts) || new Date(),
            // end date cannot come after parentPathItem due date
            after: getDate(parentPathItem.due) || null
          }
      }
    }

    // validate based on child items' dates
    const forParentItem = () => {
      const getSortedChildPathItemDates = (key) =>
        childPathItems
          .map((pathItem) => getDate(pathItem[key]))
          .filter((date) => date)
          .sort((dateA, dateB) => {
            if (dateA[key] && dateB[key]) {
              return (
                new Date(dateA[key]).getTime() - new Date(dateB[key]).getTime()
              )
            }
            if (dateA[key]) return -1
            if (dateB[key]) return 1
            return 0
          })

      const [childPathItemStartDates, childPathItemEndDates] = [
        "starts",
        "due"
      ].map(getSortedChildPathItemDates)

      switch (activeDateTab) {
        case "Start":
          return {
            // start date cannot come before now
            before: new Date(),
            // start date cannot come after the first child start date OR the last child end date
            after:
              getDate(
                first(childPathItemStartDates),
                last(childPathItemEndDates)
              ) || null
          }
        case "End":
          return {
            before:
              // end date cannot come the last child end date OR the last child start date OR the pathItem's start date
              getDate(
                last(childPathItemEndDates),
                last(childPathItemStartDates),
                pathItem.starts
              ) || new Date(),
            // end date can end anytime in the future
            after: null
          }
      }
    }

    if (pathItem) {
      if (parentPathItem) {
        return validateDisabledDays(forParentedItem())
      } else if (childPathItems.length) {
        // This level of validation could cause more confusion to the user than we want
        // return validateDisabledDays(forParentItem())
        return validateDisabledDays(forItem())
      } else {
        return validateDisabledDays(forItem())
      }
    }
  }

  const initialValues = useMemo(() => {
    if (pathItem) {
      return {
        starts: pathItem.starts ? new Date(pathItem.starts) : null,
        due: pathItem.due ? new Date(pathItem.due) : null,
        active_starts: pathItem.passive_starts === false, // defaulting starts date to passive
        active_due: pathItem.passive_due === false // defaulting due date to passive
      }
    }
  }, [pathItem])

  function getInvalidMessages(form) {
    const invalidMessages = [
      {
        field: "starts",
        messages: startingMessages
      },
      {
        field: "due",
        messages: endingMessages
      }
    ]
      .filter(
        ({ field }) =>
          field in form && !moment(form[field]).isSame(initialValues[field])
      )
      .reduce(
        (invalidMessages, { field, messages }) => [
          ...invalidMessages,
          ...messages.filter(
            (message) =>
              !form[field] ||
              (!message.sent_date &&
                moment(form[field])
                  .add(message.threshold_minutes, "minutes")
                  .isBefore())
          )
        ],
        []
      )

    return invalidMessages
  }

  function getFormValues(form) {
    let values = {
      starts: pathItem.starts,
      due: pathItem.due,
      passive_starts: pathItem.starts ? pathItem.passive_starts : null,
      passive_due: pathItem.due ? pathItem.passive_due : null
    }
    if (pendingDeletion) {
      if ("starts" in form) {
        values.starts = null
        values.passive_starts = null
      }
      if ("due" in form) {
        values.due = null
        values.passive_due = null
      }
    } else {
      if (typeof form.starts !== "undefined") {
        values.starts = form.starts ? moment(form.starts).toISOString() : null
      }
      if (typeof form.due !== "undefined") {
        values.due = form.due ? moment(form.due).toISOString() : null
      }
      if (typeof form.active_starts !== "undefined") {
        values.passive_starts = form.starts
          ? !form.active_starts
          : pathItem.passive_starts
      }
      if (typeof form.active_due !== "undefined") {
        values.passive_due = form.due ? !form.active_due : pathItem.passive_due
      }
    }
    // check if the variable values differ from the pathItem values
    const keys = ["starts", "passive_starts", "due", "passive_due"]
    if (isEqual(pick(pathItem, keys), pick(values, keys))) {
      // no change
      return null
    }

    return values
  }

  const handleSubmit = async (form) => {
    const values = getFormValues(form)
    if (!values) return

    try {
      await handleScheduleItem(values)
    } catch (error) {
      // calling formRef.current.reset has issues
      // formRef.current.reset()
      throw error // letting form handle error
    } finally {
      setPendingDeletion(false)
      setPendingConfirmation(false)
      if (pendingDeletion) {
        await messagesQuery.refetch()
      }
    }
  }

  const getDateTabLabel = (dateTab) => {
    switch (dateTab) {
      case "Start":
        return tc("starts")
      case "End":
        if (pathItem.due && pathItem.passive_due === false) {
          return tc("due")
        }
        return tc("ends")
    }
  }

  const getDateClearPrompt = (dateTab) => {
    switch (dateTab) {
      case "Start":
        return activeMessages.length
          ? tc("clear start date & reminders")
          : tc("clear start date")
      case "End":
        if (pathItem.due && pathItem.passive_due === false) {
          return activeMessages.length
            ? tc("clear due date & reminders")
            : tc("clear due date")
        }
        return activeMessages.length
          ? tc("clear end date & reminders")
          : tc("clear end date")
    }
  }

  const cardHeaderProps =
    parentPathItem && (parentPathItem.starts || parentPathItem.due)
      ? {
          headerTitle: tc("lesson: {{ lessonName }}", {
            lessonName: parentPathItem.name
          }),
          headerSubTitle: (
            <PathScheduleLabel pathItem={parentPathItem} showIcon />
          )
        }
      : {}

  return (
    <PathwrightUI.Provider
      theme={{
        backgroundImage: get(
          groupQuery,
          "data.group.resource.background_image",
          null
        ),
        backgroundOverlay: get(
          groupQuery,
          "data.group.resource.background_overlay",
          null
        )
      }}
    >
      <StyledCard
        card={card}
        title={pathItem ? pathItem.name : null}
        hideClose
        themeHeader
        navContrast={1}
        size="small"
        {...cardHeaderProps}
      >
        {pathItemQuery.error && <Alert error={pathItemQuery.error} />}
        {pathItemQuery.loading ? (
          <LoadingCircle />
        ) : !pathItem ? (
          <BlankSlate
            icons={null}
            heading={t("This step may have been deleted or does not exist.")}
            primaryAction={{
              children: `${tc("ok")}.`,
              onClick: card.onClose
            }}
          />
        ) : (
          <Form
            ref={formRef}
            onSubmit={handleSubmit}
            initialValue={initialValues}
            onChange={(form) => {
              const invalidMessages = getInvalidMessages(form)
              if (invalidMessages.length) {
                setPendingConfirmation(true)
              } else {
                // check if we should submit based on diff from form state and path item
                const values = getFormValues(form)
                if (values) {
                  formRef.current.submit()
                }
              }
            }}
            render={({ onSubmit, ...formState }) => {
              const invalidMessages = getInvalidMessages(formState.value)
              return (
                <>
                  <form onSubmit={onSubmit}>
                    <FormSubmitStatus
                      submitting={formState.submitting}
                      submitFailure={formState.submitFailure}
                      submitSuccess={formState.submitSuccess}
                      showLabel
                    >
                      <Button
                        className="Card__Action"
                        styleType="blank"
                        style={{
                          paddingRight: 0
                        }}
                        brand
                        onClick={card.onClose}
                      >
                        {tc("done")}
                      </Button>
                    </FormSubmitStatus>
                    {formState.submitFailure && (
                      <Alert error={formState.submitFailure} />
                    )}
                    {["Start", "End"].map((dateTab) => (
                      <StyledDateTab
                        key={dateTab}
                        active={dateTab === activeDateTab}
                        onClick={() => setActiveDateTab(dateTab)}
                      >
                        <div>
                          <h4>{getDateTabLabel(dateTab)}</h4>
                          <small>
                            {dateTab === "Start" ? (
                              <PathScheduleLabel
                                pathItem={pathItem}
                                due={null}
                                showIcon
                                showNotScheduled
                              />
                            ) : (
                              <PathScheduleLabel
                                pathItem={pathItem}
                                starts={null}
                                showIcon
                                showNotScheduled
                              />
                            )}
                          </small>
                        </div>
                      </StyledDateTab>
                    ))}
                    <CardBlock>
                      <Field
                        key={activeDateTab + "daytime"}
                        name={activeDateTab === "Start" ? "starts" : "due"}
                        type={"custom"}
                        label={getDateTabLabel(activeDateTab)}
                        component={DayTimePicker}
                        defaultHour={activeDateTab === "Start" ? 9 : 17}
                        disabledDays={getDisabledDays()}
                      />
                    </CardBlock>
                    {(activeDateTab === "Start" && formState.value.starts) ||
                    (activeDateTab === "End" && formState.value.due) ? (
                      <CardBlock
                        style={{
                          backgroundColor: "whitesmoke",
                          borderTopColor: "transparent",
                          marginBottom: -10,
                          borderBottomLeftRadius: 10,
                          borderBottomRightRadius: 10
                        }}
                      >
                        <Field
                          key={activeDateTab + "toggle"}
                          name={
                            activeDateTab === "Start"
                              ? "active_starts"
                              : "active_due"
                          }
                          label={
                            activeDateTab === "Start"
                              ? tc("lock content until start")
                              : tc("mark as due")
                          }
                          type="custom"
                          helperText={
                            activeDateTab === "Start"
                              ? t("Hide content under a countdown.")
                              : t(
                                  "Mark the date as ”due” and “overdue” if not completed in time."
                                )
                          }
                          onChange={(active) => {
                            // create default due reminder
                            if (
                              active &&
                              activeDateTab === "End" &&
                              !activeMessages.length
                            ) {
                              const values = getInitialMessageValues(
                                null /* message */,
                                {
                                  ...pathItem,
                                  passive_due: false // passive_due val determines result of initial values and pathItem has not yet been updated
                                },
                                "due" /* stepDateField */,
                                reminderDefaults
                              )
                              handleSubmitReminder(
                                null /* id */,
                                getMessageOutput(values)
                              )
                            }
                          }}
                          component={ToggleInput}
                          alignRight={true}
                          labelWidth={"auto"}
                        />
                        <StyledReminderList>
                          {activeMessages.map((message) => (
                            <li key={message.id}>
                              <Link
                                to={`${window.location.pathname}reminder/${message.id}/`}
                              >
                                <HStack>
                                  <Pathicon icon="bell" />
                                  <span>
                                    {capitalize(displayMessageStr(message, t))}
                                  </span>
                                  <Pathicon
                                    icon="chevron-right"
                                    style={{ marginLeft: "auto" }}
                                  />
                                </HStack>
                              </Link>
                              <HandleDefaultSubjectBody
                                cohortId={cohortId}
                                sourceId={sourceId}
                                pathItem={pathItem}
                                stepDateField={
                                  activeDateTab === "Start" ? "starts" : "due"
                                }
                                reminder={message}
                                reminderDefaults={reminderDefaults}
                              />
                            </li>
                          ))}
                          {activeMessages.length < 5 ? (
                            <li>
                              <Link
                                to={`${
                                  window.location.pathname
                                }reminder?step_date=${
                                  formState.value.starts ? "starts" : "due"
                                }`}
                                disabled={
                                  activeDateTab === "Start"
                                    ? !formState.value.starts
                                    : !formState.value.due
                                }
                              >
                                <HStack>
                                  <Pathicon icon="plus" />
                                  <span>{tc("add a reminder")}</span>
                                  <Pathicon
                                    icon="chevron-right"
                                    style={{ marginLeft: "auto" }}
                                  />
                                </HStack>
                              </Link>
                            </li>
                          ) : null}
                        </StyledReminderList>
                        <FormInlineConfirmation
                          confirmPrompt={tc("clear")}
                          onCancel={() => setPendingDeletion(false)}
                          onConfirmed={() => setPendingDeletion(false)}
                          onConfirm={(e) => setPendingDeletion(true)}
                          hideSuccess
                        >
                          {({ confirm }) => (
                            <Button
                              style={{ margin: "3px 0 1rem" }}
                              styleType="card"
                              color="red"
                              onClick={() => {
                                confirm()
                              }}
                            >
                              {/* <Pathicon
                                    icon="x"
                                    style={{ top: 0, marginRight: 2 }}
                                  /> */}
                              {getDateClearPrompt(activeDateTab)}
                            </Button>
                          )}
                        </FormInlineConfirmation>
                      </CardBlock>
                    ) : null}
                  </form>
                  {pendingConfirmation && (
                    <FormConfirmation
                      heading={tc("back to the future?")}
                      body={
                        <>
                          <p>
                            {t(
                              "Rescheduling will remove the following reminder(s) since they'll be in the past:"
                            )}
                          </p>
                          <ul style={{ textAlign: "left" }}>
                            {invalidMessages.map((message) => (
                              <li key={message.id}>
                                {capitalize(displayMessageStr(message, t))}
                              </li>
                            ))}
                          </ul>
                        </>
                      }
                      cancelPrompt={tc("cancel")}
                      confirmPrompt={tc("reschedule")}
                      onCancel={() => setPendingConfirmation(false)}
                      onConfirmed={() => setPendingConfirmation(false)}
                    />
                  )}
                </>
              )
            }}
          />
        )}
      </StyledCard>
    </PathwrightUI.Provider>
  )
}

ScheduleFormCard.displayName = "ScheduleFormCard"

ScheduleFormCard.propTypes = {
  card: PropTypes.object.isRequired,
  cohortId: PropTypes.number.isRequired,
  sourceId: PropTypes.number.isRequired
}

const FormConfirmation = withForm(({ formState, formActions, ...props }) => {
  return <FullscreenConfirm {...props} onConfirm={() => formActions.submit()} />
})

const FormInlineConfirmation = withForm(
  ({ formState, formActions, ...props }) => {
    return (
      <InlineConfirm
        {...props}
        {...formState}
        onConfirm={() => {
          props.onConfirm && props.onConfirm()
          return formActions.submit()
        }}
      />
    )
  }
)

export default ScheduleFormCard
